import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import { usePatchCategory } from 'actions/chatbot';
import LiveDot from 'screens/chatbot/components/live-dot';

import { USER_TYPE, TAG_VARIANT } from './constants';
import {
  TreeContainer,
  TreeNode,
  NodeRow,
  ExpandIcon,
  NodeLabel,
  ChildrenContainer,
  CategoryEditIcon,
  InlineInput,
  TreeSpacer,
  UserTypeTag,
} from './styled-comps';

// Map a leaf's `user_types` to a tag variant + label. Global leaves (no
// channels → empty `user_types`) get no tag, since they aren't parent- or
// teacher-specific.
function userTypeTag(userTypes) {
  const types = userTypes || [];
  const hasParent = types.includes(USER_TYPE.PARENT);
  const hasTeacher = types.includes(USER_TYPE.TEACHER);
  if (hasParent && hasTeacher) return { variant: TAG_VARIANT.BOTH, label: 'P·T' };
  if (hasParent) return { variant: TAG_VARIANT.PARENT, label: 'P' };
  if (hasTeacher) return { variant: TAG_VARIANT.TEACHER, label: 'T' };
  return null;
}

function TreeNodeItem({ node, depth = 0, selectedPath, onSelect, onCategoryRenamed }) {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(depth < 1);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const clickTimerRef = useRef(null);

  const { patch: patchCategory } = usePatchCategory({
    onComplete: errorMessage => {
      setSaving(false);
      if (errorMessage) {
        dispatch(
          showNotification({
            text: 'Failed to rename category',
            duration: 5,
            showCloseIcon: true,
          }),
        );
        cancelEditing();
      } else {
        setEditing(false);
        onCategoryRenamed && onCategoryRenamed();
      }
    },
  });

  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedPath === node.path;
  const isLeaf = node.is_leaf;
  const isCategory = node.is_category;

  // Focus the input when editing starts
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEditing = useCallback(() => {
    setEditValue(node.display_name || node.name || '');
    setEditing(true);
  }, [node.display_name, node.name]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    setEditValue('');
  }, []);

  const saveRename = useCallback(
    newName => {
      const trimmed = newName.trim();
      if (!trimmed || trimmed === (node.display_name || node.name)) {
        cancelEditing();
        return;
      }

      setSaving(true);
      patchCategory(node.path, { display_name: trimmed });
    },
    [node.path, node.display_name, node.name, cancelEditing, patchCategory],
  );

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        saveRename(editValue);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEditing();
      }
    },
    [editValue, saveRename, cancelEditing],
  );

  const handleBlur = useCallback(() => {
    if (editing && !saving) {
      saveRename(editValue);
    }
  }, [editing, saving, editValue, saveRename]);

  const handleClick = e => {
    // Don't process clicks while editing
    if (editing) return;

    // If this is a category, use a timer to distinguish single from double click
    if (isCategory) {
      if (clickTimerRef.current) {
        // Double-click detected -- clear the single-click timer
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
        startEditing();
        return;
      }

      // Start a single-click timer
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        // Single click: toggle expand and select
        setExpanded(prev => !prev);
        onSelect(node);
      }, 250);
      return;
    }

    // Non-category node: immediate click handling
    if (hasChildren) {
      setExpanded(prev => !prev);
    }
    onSelect(node);
  };

  const handleEditIconClick = e => {
    e.stopPropagation();
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    startEditing();
  };

  // Prevent click on the input from bubbling to NodeRow
  const handleInputClick = e => {
    e.stopPropagation();
  };

  return (
    <TreeNode>
      <NodeRow $depth={depth} $selected={isSelected} onClick={handleClick}>
        {hasChildren && <ExpandIcon $expanded={expanded}>&#9654;</ExpandIcon>}
        {!hasChildren && <TreeSpacer />}
        {isLeaf && <LiveDot live={node.has_published} />}
        {editing ? (
          <InlineInput
            ref={inputRef}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={handleInputClick}
            disabled={saving}
          />
        ) : (
          <>
            <NodeLabel $isCategory={isCategory}>
              {node.display_name || node.name}
            </NodeLabel>
            {isLeaf &&
              (() => {
                const tag = userTypeTag(node.user_types);
                return tag ? (
                  <UserTypeTag $variant={tag.variant}>{tag.label}</UserTypeTag>
                ) : null;
              })()}
            {isCategory && (
              <CategoryEditIcon
                title="Double-click name or click to rename"
                onClick={handleEditIconClick}
              >
                &#9998;
              </CategoryEditIcon>
            )}
          </>
        )}
      </NodeRow>
      {hasChildren && (
        <ChildrenContainer $expanded={expanded}>
          {node.children.map(child => (
            <TreeNodeItem
              key={child.path || child.id}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              onSelect={onSelect}
              onCategoryRenamed={onCategoryRenamed}
            />
          ))}
        </ChildrenContainer>
      )}
    </TreeNode>
  );
}

function TreeView({ tree, selectedPath, onSelect, onCategoryRenamed }) {
  if (!tree || tree.length === 0) {
    return null;
  }

  return (
    <TreeContainer>
      {tree.map(node => (
        <TreeNodeItem
          key={node.path || node.id}
          node={node}
          depth={0}
          selectedPath={selectedPath}
          onSelect={onSelect}
          onCategoryRenamed={onCategoryRenamed}
        />
      ))}
    </TreeContainer>
  );
}

export default TreeView;
